import type { ParseDiagnostic } from '@arctek/sysml-types';
import {
  parseSysML,
  evaluateValidationRules,
  summarizeValidation,
  formatSysmlSource,
  getFixesForDiagnostic,
} from '@arctek/sysml-core';
import type {
  LanguageServiceRequest,
  LanguageServiceResponse,
  ParseAndValidateParams,
  ParseAndValidateResult,
} from './language-service.types.js';

type PendingRequest = {
  resolve: (value: any) => void;
  reject: (reason?: unknown) => void;
};

const EMPTY_VALIDATION_SUMMARY = { error: 0, warning: 0, info: 0, totalFailed: 0 };

export class LanguageServiceClient {
  private worker: Worker | null = null;
  private workerAvailable = false;
  private nextRequestId = 1;
  private pending = new Map<number, PendingRequest>();

  constructor() {
    this.initializeWorker();
  }

  private initializeWorker(): void {
    if (typeof Worker === 'undefined') {
      this.workerAvailable = false;
      return;
    }

    try {
      this.worker = new Worker(new URL('./language-service.worker.ts', import.meta.url), {
        type: 'module',
      });

      this.worker.onmessage = (event: MessageEvent<LanguageServiceResponse>) => {
        const message = event.data;
        const pending = this.pending.get(message.id);
        if (!pending) return;
        this.pending.delete(message.id);

        if ('error' in message) {
          pending.reject(new Error(message.error));
        } else {
          pending.resolve(message.result);
        }
      };

      this.worker.onerror = () => {
        this.failWorker(new Error('Language service worker failed'));
      };

      this.workerAvailable = true;
    } catch {
      this.workerAvailable = false;
      this.worker = null;
    }
  }

  private failWorker(error: Error): void {
    this.workerAvailable = false;
    this.worker?.terminate();
    this.worker = null;
    for (const request of this.pending.values()) {
      request.reject(error);
    }
    this.pending.clear();
  }

  private async request<T>(
    method: LanguageServiceRequest['method'],
    params: LanguageServiceRequest['params'],
  ): Promise<T> {
    if (!this.workerAvailable || !this.worker) {
      throw new Error('Worker unavailable');
    }

    const id = this.nextRequestId++;
    const message: LanguageServiceRequest = { id, method, params };

    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      try {
        this.worker?.postMessage(message);
      } catch (error: unknown) {
        this.pending.delete(id);
        reject(error);
      }
    });
  }

  async parseAndValidate(
    sourceText: string,
    changeRange?: ParseAndValidateParams['changeRange'],
  ): Promise<ParseAndValidateResult> {
    if (!this.workerAvailable) {
      return this.parseAndValidateLocal(sourceText);
    }

    try {
      return await this.request<ParseAndValidateResult>('parseAndValidate', {
        sourceText,
        allowPartialModel: true,
        changeRange,
      });
    } catch {
      this.workerAvailable = false;
      return this.parseAndValidateLocal(sourceText);
    }
  }

  async format(sourceText: string): Promise<string> {
    if (!this.workerAvailable) {
      return this.formatLocal(sourceText);
    }

    try {
      return await this.request<string>('format', { sourceText });
    } catch {
      this.workerAvailable = false;
      return this.formatLocal(sourceText);
    }
  }

  async getFixes(sourceText: string, diagnostic: ParseDiagnostic): Promise<ReturnType<typeof getFixesForDiagnostic>> {
    if (!this.workerAvailable) {
      return this.getFixesLocal(sourceText, diagnostic);
    }

    try {
      return await this.request<ReturnType<typeof getFixesForDiagnostic>>('getFixes', {
        sourceText,
        diagnostic,
      });
    } catch {
      this.workerAvailable = false;
      return this.getFixesLocal(sourceText, diagnostic);
    }
  }

  dispose(): void {
    this.workerAvailable = false;
    this.worker?.terminate();
    this.worker = null;
    for (const request of this.pending.values()) {
      request.reject(new Error('Language service client disposed'));
    }
    this.pending.clear();
  }

  private parseAndValidateLocal(sourceText: string): ParseAndValidateResult {
    const parseResult = parseSysML(sourceText, { allowPartialModel: true });
    const validationReports = parseResult.model
      ? evaluateValidationRules(parseResult.model)
      : [];

    return {
      parseResult,
      validationReports,
      validationSummary: validationReports.length
        ? summarizeValidation(validationReports)
        : EMPTY_VALIDATION_SUMMARY,
    };
  }

  private formatLocal(sourceText: string): string {
    try {
      return formatSysmlSource(sourceText);
    } catch {
      return sourceText;
    }
  }

  private getFixesLocal(sourceText: string, diagnostic: ParseDiagnostic): ReturnType<typeof getFixesForDiagnostic> {
    return getFixesForDiagnostic(sourceText, diagnostic);
  }
}
